import { Alert, BrandLogo, Button, Card, Field } from '@catering/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type JSX, useState } from 'react';

import { ApiError } from '../api/client';
import { useLogin } from './use-login';

/**
 * Copied from `apps/web-admin/src/routes/login.tsx`'s flow (`design.md`
 * §6.3), not its route path: this screen sends the caller to `/orders`
 * on success, the portal's home, instead of `/`. The second-factor field
 * only appears once the server actually asks for it — no `CLIENT_*` role
 * mandates TOTP (`auth/domain/totp-policy.ts`), but an account that
 * enabled it voluntarily still needs the field.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { reset } = useSearch({ from: '/login' });
  const login = useLogin();
  const [totpRequired, setTotpRequired] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);

    try {
      await login.mutateAsync({
        email: readText(form, 'email'),
        password: readText(form, 'password'),
        ...(totpRequired ? { totpCode: readText(form, 'totpCode') } : {}),
      });

      await navigate({ to: '/orders' });
    } catch (error) {
      if (error instanceof ApiError && isTotpRequired(error)) {
        setTotpRequired(true);
      }
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-slate-50 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <BrandLogo size="lg" />
          <span className="text-sm text-slate-500">Portal de pedidos</span>
        </div>

        <Card variant="elevated" className="w-full">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
            noValidate
          >
            <header>
              <h1 className="text-lg font-semibold text-slate-900">Iniciar sesión</h1>
              <p className="mt-1 text-sm text-slate-500">Ingresá para cargar tu pedido.</p>
            </header>

            {reset === 'success' ? (
              <Alert tone="success">Tu contraseña se actualizó. Ingresá con la nueva.</Alert>
            ) : null}

            {login.isError && !totpRequired ? (
              <Alert tone="danger">{describeLoginError(login.error)}</Alert>
            ) : null}

            <Field
              label="Correo electrónico"
              name="email"
              type="email"
              autoComplete="username"
              required
              autoFocus
            />

            <Field
              label="Contraseña"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />

            {totpRequired ? (
              <Field
                label="Código de verificación"
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                hint="Seis dígitos de tu aplicación de autenticación"
                required
                autoFocus
              />
            ) : null}

            <Button type="submit" loading={login.isPending} className="w-full">
              Ingresar
            </Button>

            <Link
              to="/forgot-password"
              className="text-center text-sm font-medium text-primary-700 underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </form>
        </Card>
      </div>
    </main>
  );
}

/**
 * FormData.get returns a string or a File, and String(file) quietly yields
 * "[object File]". Narrowing here means a malformed field arrives as empty
 * text the server will reject, rather than as nonsense it might accept.
 */
function readText(form: FormData, field: string): string {
  const value = form.get(field);

  return typeof value === 'string' ? value : '';
}

function describeLoginError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Sin conexión, probá de nuevo.';
  }

  if (error.status === 401) {
    return 'Revisá tu correo y contraseña.';
  }

  if (error.status === 429) {
    return 'Hiciste demasiados intentos. Esperá unos minutos y probá de nuevo.';
  }

  return 'No pudimos ingresar. Probá de nuevo en unos minutos.';
}

function isTotpRequired(error: ApiError): boolean {
  return (error.body as { totpRequired?: boolean } | null)?.totpRequired === true;
}
